/**
 * The browser host session (ST2): this tab runs the sandbox calls a paused run lists
 * (`run.waiting[].client`, C3) for the workspaces it holds, and hands each result in with
 * `sandbox.result`.
 *
 * Per workspace: the policy (policy.ts) gives this tab the lease's session or not. Without it the
 * tab leaves the calls alone and tries again while some wait. With it the tab heartbeats the lease,
 * starts the executor on the first call, and runs the listed calls in list order, one at a time.
 * A call is keyed on (id, attempt) and taken at most once by this tab; one past its deadline is not
 * started (the server tells the model it timed out), also when the executor's boot ran past it. A
 * lost lease (a refused heartbeat or delta, or a hand-in refused as not the holder's) stops the
 * workspace: the running call is cancelled, nothing is handed in for it, and the executor is thrown
 * away, since another host now changes the files. Its other calls wait for the policy to give the
 * lease back. A call cut before anything was handed in for it may run here again if it is still
 * listed once this tab holds the lease again; one whose answer went out is never run here again
 * (ER5 reroutes it). After each call the workspace's sync engine (sync/engine.ts) sends what it
 * changed, and the call's answer goes in only once the server holds that (waiting until
 * SAVE_RESERVE_MS before the call's deadline), so a reload right after the answer finds the files;
 * a result whose changes did not all get there goes in as UNSAVED, as the hosted executor's does.
 * A running call the runs stop listing (answered elsewhere, timed out, cancelled) for a
 * heartbeat's time is cancelled and handed nothing; its changes go with the next debounced round.
 * What the server changes under a held workspace (a user's upload, `workspace.changed`, and after
 * a reconnect whatever a lost frame said) is pulled into its executor between calls, before the
 * next one (WS3, pickup.ts); a pickup that failed is tried again, backing off, a few times. A
 * synced batch of this tab's own that changed a Files panel path also sends the frame: that echo
 * costs one listing and pulls nothing.
 *
 * A planned release (ER4, spec 3.2) gives a lease up on purpose: when this tab stops being the host
 * (`handOver`, followHostRole), and for the least recently used idle workspace past the hot cap
 * (MAX_HOT_PER_USER). It takes no new call and keeps the lease alive (heartbeats) while the running
 * call finishes and hands in, bounded by that call's deadline: a cut call has committed part of its
 * writes already (stage commits per file), so a rerun elsewhere would apply them twice. Then it
 * flushes the sync engine's unsent changes for up to HANDOVER_FLUSH_MS, stops the executor and
 * releases: the next host acquires at the next epoch and revives from what was committed. Only a
 * loss cuts a running call.
 *
 * A preview wants a workspace (`want`) as a listed call does, before any call: the tab tries for
 * its lease (by the same policy, again each heartbeat while it stands by) and, once it holds it,
 * starts the executor, which is what the preview reads. So a tab that hosts nothing yet, the only
 * one after a reload say, takes the workspace up when a preview of it shows.
 */
import type { AgentState } from "../../store/agentSlice";
import { SandboxError, toErrorData } from "../core/errors";
import type { SandboxClientCall, SandboxHandIn } from "../core/hostCalls";
import type { Executor } from "../core/types";
import { LeaseError } from "../sync/api";
import {
  SyncIncomplete,
  SyncStopped,
  withSync,
  type SyncEngine,
  type SyncEngineDeps,
} from "../sync/engine";
import type { HostSession } from "../sync/session";
import { runSandboxOp, wellFormed } from "./ops";
import { pickUpFiles, type PanelFiles } from "./pickup";
import { standsBy, type HostPolicy } from "./policy";
import type { SandboxHost as HostFrame } from "./role";

/** backend routing.HEARTBEAT_SECONDS, on the 15 s lease. */
export const HEARTBEAT_MS = 5_000;
/** backend executor.ERROR_MESSAGE_MAX. */
const ERROR_MESSAGE_MAX = 2000;
/** backend hosted/pool.RAN_UNSAVED: what the model hears of a call whose changes were not saved. */
const RAN_UNSAVED = "The command ran, but some of its changes could not be saved: ";
/**
 * A call's answer waits for its changes until this long before its deadline (backend
 * hosted/pool.CAP_RESERVE_SECONDS), so it reaches the server before the sweep times the call out.
 */
const SAVE_RESERVE_MS = 5_000;
/** A call that ended inside the reserve still waits this long, at most half of what it has left. */
const SAVE_FLOOR_MS = 1_000;
const HAND_IN_RETRY_MS = 1_000;
const HAND_IN_RETRY_MAX_MS = 5_000;
const PICKUP_RETRY_MS = 1_000;
const PICKUP_RETRY_MAX_MS = 30_000;
/** Failed pickups in a row before the host waits for the next frame or reconnect. */
const PICKUP_TRIES = 5;
/** How long a planned release flushes before it gives the lease up anyway (spec 3.2). */
export const HANDOVER_FLUSH_MS = 2_000;
/**
 * Executors this tab keeps running at once (spec section 10, SANDBOX_MAX_HOT_PER_USER): one tab
 * hosts a user's on-device work, so this is the user's cap there.
 */
export const MAX_HOT_PER_USER = 3;

/**
 * An executor this host started for a workspace; `dispose` frees what kill leaves (a frame),
 * `synced` is the restore index it was revived with (what the server already stores).
 */
export type HostedExecutor = Executor & { dispose?(): void; synced?: Record<string, string> };

/** A `sandbox.result` that did not go through: `forbidden` means this tab no longer holds it. */
export interface HandInError {
  code: string;
  message: string;
}

/** The call an executor runs now: the client id its cells' tool calls go under (CM3's `call`). */
export interface ServedCall {
  id: string;
  /** Epoch ms; null when the server's deadline does not parse (its cell's own clock still ends it). */
  deadline: number | null;
}

export interface SandboxHostDeps {
  /** This tab's id: the lease owner the hand-in is checked against. */
  owner: string;
  policy: HostPolicy;
  /**
   * A started executor for the workspace, revived from the server's copy of it. `serving`: the
   * repl call it runs now, while this hold has it and has not dropped it; null otherwise.
   */
  startExecutor(workspaceId: string, serving: () => ServedCall | null): Promise<HostedExecutor>;
  /** Sends `sandbox.result`; rejects with a `HandInError`. */
  handIn(answer: SandboxHandIn): Promise<{ fired: boolean }>;
  /** Starts the sync engine of a held workspace; without it nothing the calls change is synced. */
  sync?(hooks: Pick<SyncEngineDeps, "session" | "source" | "onLeaseLost">): SyncEngine;
  /** The Files panel REST; without it `workspaceChanged` does nothing. */
  files?: PanelFiles;
  log?: Pick<Console, "warn">;
  timers: {
    now(): number;
    setTimeout(callback: () => void, ms: number): unknown;
    clearTimeout(handle: unknown): void;
  };
  heartbeatMs?: number;
  /** MAX_HOT_PER_USER unless given. */
  maxHot?: number;
}

/** The call a hold runs now. */
interface Running {
  call: SandboxClientCall;
  /** No longer listed for a heartbeat's time: cancelled, and handed nothing. */
  dropped: boolean;
  /** Pending while the call is unlisted, before it counts as dropped. */
  dropTimer: unknown;
  /** Rechecks the call at its deadline, when it counts as de-listed. */
  deadlineTimer: unknown;
  /** Its answer went out (it may have fired): never run here again, even when cut. */
  handedIn: boolean;
  /** A watched run listed it: its de-listing is the run's word (answered, stopped), heeded even
   * while releasing. */
  ofRun: boolean;
  /** Resolves when the call is done here: finished, or cut by the hold's end. */
  settled: Promise<void>;
  settle(): void;
}

interface Hold {
  workspaceId: string;
  /** `releasing`: a planned release is flushing; no new call starts. */
  status: "acquiring" | "held" | "releasing" | "standby" | "stopped";
  session: HostSession | null;
  sync: SyncEngine | null;
  executor: HostedExecutor | null;
  queue: SandboxClientCall[];
  running: Running | null;
  timer: unknown;
  /** Moves when the hold ends: work begun under an older one hands nothing in. */
  generation: number;
  /** A pickup waits for the running call; `picking`: one runs, and calls wait for it. */
  pickup: boolean;
  picking: boolean;
  /** Pickups that failed in a row, and the timer of the next try. */
  pickupFailures: number;
  pickupRetry: unknown;
  /** When a call last started or ended here: the hot cap suspends the least recent. */
  usedAt: number;
  /** Counts the tries to take the lease: only the latest one's answer counts. */
  attempts: number;
  /** The lease a superseded try got while a later one was still out: the same lease, kept to be
   * released if the later try ends without it. */
  spare: HostSession | null;
  /** The executor's start while it boots: a call and a preview share it. */
  starting: Promise<HostedExecutor | null> | null;
  /** When the latest try to take the lease began (a preview's `want` tries at most once a beat). */
  triedAt: number;
}

const callKey = (call: SandboxClientCall) => `${call.id}:${call.attempt}`;

function isSandboxCall(value: unknown): value is SandboxClientCall {
  const call = value as Partial<SandboxClientCall> | null;
  return (
    !!call &&
    call.kind === "sandbox" &&
    typeof call.id === "string" &&
    typeof call.workspace === "string" &&
    typeof call.op === "string" &&
    typeof call.deadline === "string"
  );
}

export function createSandboxHost(deps: SandboxHostDeps) {
  const { timers } = deps;
  const log = deps.log ?? console;
  const heartbeatMs = deps.heartbeatMs ?? HEARTBEAT_MS;
  const maxHot = deps.maxHot ?? MAX_HOT_PER_USER;
  const holds = new Map<string, Hold>();
  /** Calls this tab took: never run again here. */
  const done = new Set<string>();
  let listed: SandboxClientCall[] = [];
  /** The ids of the listed calls a watched run lists (not only `sandbox.waiting`). */
  let ofRuns: ReadonlySet<string> = new Set();
  /** This tab may host: false from a hand-over until the next retry. */
  let hosting = true;
  let disposed = false;
  /** How many previews want each workspace (`want`). */
  const wanted = new Map<string, number>();
  const changed = new Set<() => void>();

  const listedFor = (workspaceId: string) =>
    listed.filter((call) => call.workspace === workspaceId);
  /** Something wants the workspace's lease: a listed call or a preview. */
  const needed = (workspaceId: string) =>
    listedFor(workspaceId).length > 0 || wanted.has(workspaceId);
  /** A hold took, lost or started something: previews look again. */
  const notify = () => {
    for (const listener of [...changed]) listener();
  };
  const isListed = (call: SandboxClientCall) =>
    listed.some((listedCall) => callKey(listedCall) === callKey(call));
  const pastDeadline = (call: SandboxClientCall) => timers.now() >= Date.parse(call.deadline);
  /** While releasing, a running call no run listed keeps going when it is de-listed: standing by
   * empties `sandbox.waiting`, which may be all that listed it. */
  const standsAside = (hold: Hold, running: Running) =>
    hold.status === "releasing" && !running.ofRun;

  /** The spare lease is given up: no try that could hold it is left. */
  function releaseSpare(hold: Hold) {
    const { spare } = hold;
    hold.spare = null;
    if (spare) void spare.release().catch(() => undefined);
  }

  function schedule(hold: Hold, callback: () => void, ms = heartbeatMs) {
    timers.clearTimeout(hold.timer);
    hold.timer = timers.setTimeout(() => {
      hold.timer = null;
      callback();
    }, ms);
  }

  async function acquire(hold: Hold) {
    const attempt = ++hold.attempts;
    hold.status = "acquiring";
    hold.triedAt = timers.now();
    let session: HostSession | null = null;
    try {
      session = await deps.policy(hold.workspaceId, deps.owner);
    } catch {
      session = null; // unreachable or refused: stand by and try again
    }
    if (disposed || attempt !== hold.attempts || hold.status !== "acquiring") {
      // Given up, or a later try (a role flap) took over. Tries of one owner share the lease's
      // epoch, so releasing this session would clear one this tab holds or is taking now: while a
      // later try is out it is kept as the spare, for that try to release if it ends without it.
      if (hold.session) return;
      if (session) hold.spare ??= session;
      if (hold.status !== "acquiring") releaseSpare(hold); // no try is out any more
      return;
    }
    if (!session) {
      releaseSpare(hold);
      standBy(hold);
      return;
    }
    hold.spare = null; // the same lease as this one's: kept, not released
    const engine = deps.sync?.({
      session,
      source: () => hold.executor,
      onLeaseLost: () => {
        if (hold.sync === engine) lose(hold);
      },
    });
    hold.sync = engine ?? null;
    hold.session = engine ? withSync(session, engine) : session;
    hold.status = "held";
    schedule(hold, () => void beat(hold));
    pump(hold);
    warm(hold);
    notify();
  }

  /** Not this tab's: try again later while calls or a preview want it. */
  function standBy(hold: Hold) {
    hold.status = "standby";
    hold.queue = [];
    if (needed(hold.workspaceId)) schedule(hold, () => void acquire(hold));
    notify();
  }

  /** A held workspace a preview wants starts its executor now, not on its first call. */
  function warm(hold: Hold) {
    if (hold.status !== "held" || hold.executor || !wanted.has(hold.workspaceId)) return;
    void executorFor(hold, hold.generation).catch((error: unknown) => {
      log.warn("sandbox host: the workspace's executor could not start for its preview", error);
    });
  }

  function holdFor(workspaceId: string): Hold {
    let hold = holds.get(workspaceId);
    if (hold) return hold;
    hold = {
      workspaceId,
      status: "standby",
      session: null,
      sync: null,
      executor: null,
      queue: [],
      running: null,
      timer: null,
      generation: 0,
      pickup: false,
      picking: false,
      pickupFailures: 0,
      pickupRetry: null,
      usedAt: timers.now(),
      attempts: 0,
      spare: null,
      starting: null,
      triedAt: -Infinity,
    };
    holds.set(workspaceId, hold);
    void acquire(hold);
    return hold;
  }

  /** Clears the running call's timers: it is done here. */
  function stopTimers(running: Running) {
    timers.clearTimeout(running.dropTimer);
    timers.clearTimeout(running.deadlineTimer);
    running.deadlineTimer = null;
  }

  /** The lease is this tab's to keep alive: held, or releasing while the running call finishes. */
  const alive = (hold: Hold) => hold.status === "held" || hold.status === "releasing";

  async function beat(hold: Hold) {
    const { session, generation } = hold;
    if (!session || !alive(hold)) return;
    try {
      await session.heartbeat();
    } catch (error) {
      if (hold.generation !== generation) return;
      if (error instanceof LeaseError) {
        lose(hold);
        return;
      }
      // A transient failure: the lease outlives a missed beat.
    }
    if (hold.generation === generation && alive(hold)) schedule(hold, () => void beat(hold));
  }

  /** The lease is gone: stop, cancel the running call, hand nothing in, drop the executor. */
  function lose(hold: Hold) {
    end(hold);
    standBy(hold);
  }

  function end(hold: Hold) {
    releaseSpare(hold);
    hold.generation++;
    timers.clearTimeout(hold.timer);
    hold.timer = null;
    hold.session = null;
    hold.sync?.stop();
    hold.sync = null;
    hold.queue = [];
    hold.pickup = hold.picking = false;
    hold.pickupFailures = 0;
    timers.clearTimeout(hold.pickupRetry);
    hold.pickupRetry = null;
    const { executor, running } = hold;
    hold.executor = null;
    hold.starting = null; // a boot still out is discarded when it ends (executorFor)
    hold.running = null;
    if (running) {
      stopTimers(running);
      if (!running.handedIn) done.delete(callKey(running.call)); // cut unanswered: may run again
      running.settle();
    }
    if (executor) discard(executor, !!running);
  }

  function discard(executor: HostedExecutor, cancel: boolean) {
    const cancelled = cancel ? executor.cancel().catch(() => undefined) : Promise.resolve();
    void cancelled
      .then(() => executor.kill())
      .catch(() => undefined)
      .finally(() => executor.dispose?.());
  }

  function enqueue(hold: Hold) {
    const queued = new Set(hold.queue.map(callKey));
    if (hold.running) queued.add(callKey(hold.running.call));
    for (const call of listedFor(hold.workspaceId)) {
      const key = callKey(call);
      if (done.has(key) || queued.has(key)) continue;
      queued.add(key);
      hold.queue.push(call);
    }
  }

  function pump(hold: Hold) {
    if (disposed || hold.status !== "held" || hold.running || hold.picking) return;
    if (hold.pickup) {
      pickUp(hold);
      return;
    }
    enqueue(hold);
    while (hold.queue.length) {
      const call = hold.queue.shift()!;
      const key = callKey(call);
      if (done.has(key)) continue;
      done.add(key);
      if (pastDeadline(call)) continue;
      let settle!: () => void;
      const settled = new Promise<void>((resolve) => {
        settle = resolve;
      });
      const running: Running = {
        call,
        dropped: false,
        dropTimer: null,
        deadlineTimer: null,
        handedIn: false,
        ofRun: ofRuns.has(call.id),
        settled,
        settle,
      };
      hold.running = running;
      running.deadlineTimer = timers.setTimeout(
        () => {
          running.deadlineTimer = null;
          if (hold.running === running) watchRunning(hold);
        },
        Math.max(0, Date.parse(call.deadline) - timers.now())
      );
      hold.usedAt = timers.now();
      void run(hold, running);
      return;
    }
  }

  /** A pickup is due: before the next call, or now when none runs. */
  function wantPickup(hold: Hold) {
    hold.pickup = true;
    pump(hold);
  }

  /**
   * Pulls in what the server changed (an upload, an export), between calls; the next call waits
   * for it.
   */
  function pickUp(hold: Hold) {
    hold.pickup = false;
    timers.clearTimeout(hold.pickupRetry);
    hold.pickupRetry = null;
    const { executor, generation } = hold;
    // Not started: its start restores the server's copy.
    if (!executor || !deps.files) {
      pump(hold);
      return;
    }
    hold.picking = true;
    const current = () => hold.generation === generation && !disposed;
    void pickUpFiles(hold.workspaceId, executor, hold.sync, deps.files, current, log)
      .then(
        ({ failed }) => failed === 0,
        (error: unknown) => {
          log.warn("sandbox host: the workspace's files could not be listed", error);
          return false;
        }
      )
      .then((ok) => {
        if (!current()) return;
        hold.picking = false;
        hold.pickupFailures = ok ? 0 : hold.pickupFailures + 1;
        if (!ok && hold.pickupFailures < PICKUP_TRIES) {
          const wait = Math.min(
            PICKUP_RETRY_MS * 2 ** (hold.pickupFailures - 1),
            PICKUP_RETRY_MAX_MS
          );
          hold.pickupRetry = timers.setTimeout(() => {
            hold.pickupRetry = null;
            wantPickup(hold);
          }, wait);
        }
        pump(hold);
      });
  }

  /**
   * The repl call a hold's executor runs now. Bound to the hold that started the executor: once
   * that hold ends, a straggler from its executor is never taken for the next hold's call.
   */
  function serving(hold: Hold, generation: number): ServedCall | null {
    const { running } = hold;
    if (disposed || hold.generation !== generation || !running || running.dropped) return null;
    if (running.call.op !== "repl") return null;
    const deadline = Date.parse(running.call.deadline);
    return { id: running.call.id, deadline: Number.isFinite(deadline) ? deadline : null };
  }

  /**
   * The executor, started on the workspace's first call (or for its preview); null when the hold
   * ended meanwhile. One start at a time per hold.
   */
  async function executorFor(hold: Hold, generation: number): Promise<HostedExecutor | null> {
    if (hold.executor) return hold.executor;
    if (!hold.starting) {
      makeRoom(hold);
      const served = () => serving(hold, generation);
      const starting = deps.startExecutor(hold.workspaceId, served).then(
        (executor) => {
          if (hold.starting === starting) hold.starting = null;
          if (hold.generation !== generation || disposed) {
            discard(executor, false); // lost or disposed during the boot: never kept on a dead hold
            return null;
          }
          hold.executor = executor;
          notify();
          return executor;
        },
        (error: unknown) => {
          if (hold.starting === starting) hold.starting = null;
          notify();
          throw error;
        }
      );
      hold.starting = starting;
    }
    const executor = await hold.starting;
    return hold.generation === generation ? executor : null;
  }

  async function run(hold: Hold, running: Running) {
    const { call } = running;
    const generation = hold.generation;
    let answer: SandboxHandIn;
    try {
      const executor = await executorFor(hold, generation);
      if (!executor || hold.generation !== generation) return;
      await hold.sync?.beforeCall(); // what a pickup wrote before it is not the call's to save
      if (hold.generation !== generation || disposed) return;
      if (running.dropped || pastDeadline(call)) {
        hold.sync?.afterCall(); // what that drain queued goes with the next debounced round
        finish(hold, running); // the boot or the drain outlasted it: start nothing
        return;
      }
      const result = await runSandboxOp(executor, hold.session!, call);
      answer = { call: call.id, result };
    } catch (error) {
      if (hold.generation !== generation) return;
      if (error instanceof LeaseError) {
        lose(hold);
        return;
      }
      const { code, message } = toErrorData(error);
      answer = { call: call.id, error: { code, message: wellFormed(message, ERROR_MESSAGE_MAX) } };
    }
    if (hold.generation !== generation || disposed) return;
    if (running.dropped) {
      hold.sync?.afterCall(); // never handed in: nothing waits on its changes
    } else {
      answer = await saved(hold, call, answer);
      if (hold.generation !== generation || disposed) return;
    }
    if (!running.dropped) await handIn(hold, generation, running, answer);
    if (hold.generation !== generation) return;
    finish(hold, running);
  }

  /**
   * The answer once the server holds what the call changed (a reload right after it must find the
   * files), waiting until SAVE_RESERVE_MS before the call's deadline, so the answer still beats
   * the server's timeout. A result whose changes did not all get there goes in as UNSAVED; an op's
   * error stays its own.
   */
  async function saved(
    hold: Hold,
    call: SandboxClientCall,
    answer: SandboxHandIn
  ): Promise<SandboxHandIn> {
    if (!hold.sync) return answer;
    let failure: unknown = null;
    let done = false;
    const saving = hold.sync.saveCall().then(
      () => (done = true),
      (error: unknown) => {
        failure = error;
        done = true;
      }
    );
    const left = Date.parse(call.deadline) - timers.now();
    await within(saving, Math.max(left - SAVE_RESERVE_MS, Math.min(SAVE_FLOOR_MS, left / 2)));
    if ((done && !failure) || failure instanceof SyncStopped || answer.error) return answer;
    const detail = !done
      ? "they were still being sent when the call's time ran out"
      : failure instanceof SyncIncomplete
        ? `${failure.unsent} of its file changes did not reach the server`
        : `they could not be sent (${toErrorData(failure).message})`;
    const message = wellFormed(`${RAN_UNSAVED}${detail}.`, ERROR_MESSAGE_MAX);
    return { call: call.id, error: { code: "UNSAVED", message } };
  }

  function finish(hold: Hold, running: Running) {
    stopTimers(running);
    if (hold.running === running) hold.running = null;
    hold.usedAt = timers.now();
    running.settle();
    pump(hold);
  }

  /**
   * Past the hot cap, suspend the least recently used workspace that is idle (nothing running,
   * nothing listed). With none idle the new one starts anyway: a call is never held back for it.
   */
  function makeRoom(starting: Hold) {
    const hot = [...holds.values()].filter(
      (hold) => hold !== starting && hold.status === "held" && hold.executor
    );
    if (hot.length < maxHot) return;
    const idle = hot
      .filter((hold) => !hold.running && !needed(hold.workspaceId))
      .sort((a, b) => a.usedAt - b.usedAt);
    if (idle.length) void release(idle[0]);
  }

  /** Waits for `promise`, for at most `ms`. */
  async function within(promise: Promise<unknown>, ms: number) {
    let waited: unknown = null;
    await Promise.race([
      promise,
      new Promise<void>((resolve) => {
        waited = timers.setTimeout(resolve, Math.max(0, ms));
      }),
    ]);
    timers.clearTimeout(waited);
  }

  /**
   * A planned release: no new call; the running one finishes and hands in (by its deadline); then
   * flush for up to HANDOVER_FLUSH_MS, stop and give the lease up.
   */
  async function release(hold: Hold) {
    const { session, generation } = hold;
    if (hold.status !== "held" || !session) return;
    hold.status = "releasing";
    watchRunning(hold); // no drop while it finishes
    const releasing = () => hold.generation === generation && hold.status === "releasing";
    const running = hold.running;
    if (running) await within(running.settled, Date.parse(running.call.deadline) - timers.now());
    if (!releasing()) return; // lost meanwhile
    if (running && hold.running === running) {
      // Past its deadline: the server answered it as timed out. Stop it before the flush.
      running.dropped = true;
      void hold.executor?.cancel().catch(() => undefined);
    }
    // A lost lease or a failed send: release anyway.
    await within(
      session.flush().catch(() => undefined),
      HANDOVER_FLUSH_MS
    );
    if (!releasing()) return;
    end(hold);
    await session.release().catch(() => undefined);
    if (hold.status !== "releasing") return;
    // The role came back while it released (retry skips a releasing hold): take the lease again now.
    if (hosting && needed(hold.workspaceId)) void acquire(hold);
    else standBy(hold);
  }

  /** Follows whether the running call is still listed: one gone for a heartbeat's time (answered
   * elsewhere, timed out, cancelled) is cancelled and handed nothing; a blip in the list is not.
   * The server lists a stopped or timed-out call's absence to the host in `sandbox.waiting` too; a
   * call past its deadline counts as gone even if that frame never came (the server answered it).
   * Not while releasing, for a call no watched run listed (`standsAside`): it finishes and hands in
   * (the lease keeps other hosts off; release bounds it by its deadline). A run's de-listing (a
   * Stop) still drops it. */
  function watchRunning(hold: Hold) {
    const running = hold.running;
    if (!running || running.dropped) return;
    if (ofRuns.has(running.call.id)) running.ofRun = true;
    const stillListed = (call: SandboxClientCall) => isListed(call) && !pastDeadline(call);
    if (stillListed(running.call) || standsAside(hold, running)) {
      timers.clearTimeout(running.dropTimer);
      running.dropTimer = null;
      return;
    }
    if (running.dropTimer) return;
    running.dropTimer = timers.setTimeout(() => {
      running.dropTimer = null;
      if (hold.running !== running || stillListed(running.call) || standsAside(hold, running)) {
        return;
      }
      running.dropped = true;
      void hold.executor?.cancel().catch(() => undefined);
    }, heartbeatMs);
  }

  /** Sends the answer; while the socket is down, tries again for as long as the call is listed
   * (or its hold is releasing and no run listed it) and before its deadline (a reconnect lists it again, or drops it
   * once answered). */
  async function handIn(hold: Hold, generation: number, running: Running, answer: SandboxHandIn) {
    const { call } = running;
    for (let tries = 1; ; tries++) {
      try {
        running.handedIn = true;
        await deps.handIn(answer);
        return; // fired false: answered already (or timed out); either way done
      } catch (error) {
        const code = (error as Partial<HandInError> | null)?.code;
        if (hold.generation !== generation || disposed) return;
        if (code === "forbidden") {
          lose(hold);
          return;
        }
        if (code !== "disconnected" && code !== "unavailable") return;
        const wait = Math.min(HAND_IN_RETRY_MS * tries, HAND_IN_RETRY_MAX_MS);
        await new Promise<void>((resolve) => timers.setTimeout(resolve, wait));
        if (hold.generation !== generation || disposed) return;
        if (running.dropped || pastDeadline(call)) return;
        if (!isListed(call) && !standsAside(hold, running)) return;
      }
    }
  }

  return {
    /** The calls listed now; `fromRuns`, the ids of those a watched run lists (every thread's
     * `run.waiting[].client`), the rest coming only from `sandbox.waiting`. */
    update(waiting: readonly unknown[], fromRuns: Iterable<string> = []) {
      if (disposed) return;
      listed = waiting.filter(isSandboxCall);
      ofRuns = new Set(fromRuns);
      const workspaces = new Set(listed.map((call) => call.workspace));
      for (const workspaceId of workspaces) {
        const hold = holds.get(workspaceId);
        if (!hold) holdFor(workspaceId);
        else if (hold.status === "standby" && !hold.timer) schedule(hold, () => void acquire(hold));
      }
      for (const hold of holds.values()) {
        // A call no longer listed was answered elsewhere, timed out or was cancelled.
        const keys = new Set(listedFor(hold.workspaceId).map(callKey));
        hold.queue = hold.queue.filter((call) => keys.has(callKey(call)));
        watchRunning(hold);
        pump(hold);
      }
    },

    /**
     * `workspace.changed`: a workspace's files changed (a user's upload, or a synced batch: the
     * holder's own echo). A held one whose executor runs pulls them in before its next call
     * (frames meanwhile make one pickup). One not held or not started restores the server's copy.
     */
    workspaceChanged(workspaceId: string) {
      const hold = holds.get(workspaceId);
      if (disposed || !deps.files || hold?.status !== "held") return;
      hold.pickupFailures = 0;
      wantPickup(hold);
    },

    /** The socket is back: a frame sent while it was down is lost, so every held workspace looks. */
    reconnected() {
      if (disposed || !deps.files) return;
      for (const hold of holds.values()) {
        if (hold.status !== "held" || !hold.executor) continue;
        hold.pickupFailures = 0;
        wantPickup(hold);
      }
    },

    /**
     * This tab stopped being the host (ER4): release every workspace it holds, as planned. One
     * still acquiring stands by, so the lease it gets back is released at once (acquire).
     */
    async handOver() {
      hosting = false;
      for (const hold of holds.values()) if (hold.status === "acquiring") standBy(hold);
      await Promise.all([...holds.values()].map((hold) => release(hold)));
    },

    /** This tab may host now: try every standing-by workspace at once, not a heartbeat later. */
    retry() {
      if (disposed) return;
      hosting = true;
      for (const hold of holds.values()) {
        if (hold.status !== "standby" || !needed(hold.workspaceId)) continue;
        timers.clearTimeout(hold.timer);
        hold.timer = null;
        void acquire(hold);
      }
    },

    /**
     * A preview wants the workspace: this tab tries for its lease now (when it may host, and its
     * last try is a beat old: Try again or a second card send no extra request) and, once it holds
     * it, starts its executor. Returns the preview's release of its want; the hold stays as one a
     * call left behind (idle, suspended past the hot cap).
     */
    want(workspaceId: string): () => void {
      if (disposed) return () => undefined;
      wanted.set(workspaceId, (wanted.get(workspaceId) ?? 0) + 1);
      const known = holds.get(workspaceId);
      const hold = holdFor(workspaceId);
      if (hold === known) {
        if (hold.status === "held") warm(hold);
        else if (hold.status === "standby" && hosting) {
          const since = timers.now() - hold.triedAt;
          if (since < heartbeatMs) {
            // A beat after the last try, not after this want.
            if (!hold.timer) schedule(hold, () => void acquire(hold), heartbeatMs - since);
          } else {
            timers.clearTimeout(hold.timer);
            hold.timer = null;
            void acquire(hold);
          }
        }
      }
      let wants = true;
      return () => {
        if (!wants) return;
        wants = false;
        const left = (wanted.get(workspaceId) ?? 1) - 1;
        if (left > 0) {
          wanted.set(workspaceId, left);
          return;
        }
        wanted.delete(workspaceId);
        // Standing by with nothing else wanting it: no more tries for a card that is gone.
        const hold = holds.get(workspaceId);
        if (hold?.status === "standby" && !needed(workspaceId)) {
          timers.clearTimeout(hold.timer);
          hold.timer = null;
        }
      };
    },

    /** Calls `listener` whenever a hold is taken, started, lost or stood down; returns its removal. */
    onChange(listener: () => void): () => void {
      changed.add(listener);
      return () => changed.delete(listener);
    },

    /** The tab went (`dispose`): a preview waiting on this host claims on the next one. */
    isDisposed(): boolean {
      return disposed;
    },

    /** Which workspaces this tab holds (for the UI and tests). */
    holding(workspaceId: string): boolean {
      return holds.get(workspaceId)?.status === "held";
    },

    /**
     * Read-only access to a workspace this tab holds and has started (for its previews), else
     * null. It reads only while that hold lasts: once lost or ended, every read is refused.
     */
    reader(workspaceId: string): Pick<Executor, "list" | "stat" | "exportFile"> | null {
      const hold = holds.get(workspaceId);
      if (disposed || !hold || hold.status !== "held" || !hold.executor) return null;
      const { generation, executor } = hold;
      const live = () => {
        if (disposed || hold.generation !== generation || hold.status !== "held") {
          throw new SandboxError("NOT_STARTED", "NOT_STARTED: the sandbox stopped in this tab");
        }
        return executor;
      };
      return {
        list: async (path) => live().list(path),
        stat: async (path) => live().stat(path),
        exportFile: async (path) => live().exportFile(path),
      };
    },

    /** The tab is going: give every lease up and stop every executor. */
    async dispose() {
      disposed = true;
      const releases: Promise<void>[] = [];
      for (const hold of holds.values()) {
        const session = hold.session;
        end(hold);
        hold.status = "stopped";
        if (session) releases.push(session.release().catch(() => undefined));
      }
      holds.clear();
      wanted.clear();
      notify();
      changed.clear();
      await Promise.all(releases);
    },
  };
}

export type SandboxHost = ReturnType<typeof createSandboxHost>;

/**
 * Feeds the host the calls listed now, once each by call id: every run's listed calls
 * (`run.waiting[].client`), and as the host tab the user's waiting calls from any thread or surface
 * (`sandbox.waiting`). It re-syncs when the threads or `sandbox.waiting` change. A thread that leaves
 * the store (its panel closed, its page left) still waits on the server, so its calls as last seen
 * stay listed until their deadlines: only a thread still in the store that no longer lists a call
 * de-lists it (a call the hosted pool took, C13, leaves at once), or a thread whose access was lost
 * (its target `lost`).
 */
export function followWaitingCalls(
  store: { getState(): { agent: AgentState }; subscribe(listener: () => void): () => void },
  host: Pick<SandboxHost, "update">,
  timers: Pick<SandboxHostDeps["timers"], "now" | "setTimeout" | "clearTimeout"> = {
    now: () => Date.now(),
    setTimeout: (callback, ms) => setTimeout(callback, ms),
    clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
  }
): () => void {
  let seenThreads: AgentState["threads"] | null = null;
  let seenWaiting: AgentState["sandboxWaiting"] | null = null;
  const lastSeen = new Map<string, SandboxClientCall[]>();
  let expiry: unknown = null;
  const sync = (force = false) => {
    const { threads, targets, sandboxWaiting } = store.getState().agent;
    if (threads === seenThreads && sandboxWaiting === seenWaiting && !force) return;
    seenThreads = threads;
    seenWaiting = sandboxWaiting;
    const now = timers.now();
    const lost = new Set(
      Object.values(targets).flatMap((target) =>
        target.lost && target.threadId != null ? [String(target.threadId)] : []
      )
    );
    let next = Infinity;
    for (const [id, thread] of Object.entries(threads)) {
      lastSeen.set(
        id,
        (thread.run?.waiting ?? []).flatMap((wait) =>
          isSandboxCall(wait.client) ? [wait.client] : []
        )
      );
    }
    for (const [id, calls] of lastSeen) {
      if (id in threads) continue;
      const kept = lost.has(id) ? [] : calls.filter((call) => Date.parse(call.deadline) > now);
      if (kept.length) lastSeen.set(id, kept);
      else lastSeen.delete(id);
      for (const call of kept) next = Math.min(next, Date.parse(call.deadline));
    }
    timers.clearTimeout(expiry);
    expiry = next < Infinity ? timers.setTimeout(() => sync(true), next - now) : null;
    const listed = new Map<string, SandboxClientCall>();
    const ofRuns = [...lastSeen.values()].flat();
    for (const call of [...sandboxWaiting.filter(isSandboxCall), ...ofRuns]) {
      if (!listed.has(call.id)) listed.set(call.id, call);
    }
    host.update([...listed.values()], new Set(ofRuns.map((call) => call.id)));
  };
  sync();
  const unsubscribe = store.subscribe(() => sync());
  return () => {
    unsubscribe();
    timers.clearTimeout(expiry);
  };
}

/** Feeds the host every `workspace.changed` frame the agent socket receives, and its reconnects. */
export function followWorkspaceChanges(
  client: {
    onWorkspaceChanged(listener: (frame: { workspace: string }) => void): () => void;
    onReconnected(listener: () => void): () => void;
  },
  host: Pick<SandboxHost, "workspaceChanged" | "reconnected">
): () => void {
  const stopFrames = client.onWorkspaceChanged((frame) => host.workspaceChanged(frame.workspace));
  const stopReconnects = client.onReconnected(() => host.reconnected());
  return () => {
    stopFrames();
    stopReconnects();
  };
}

/**
 * Follows this tab's part in the election (`sandbox.host`, ER4): once it stands by (another tab or
 * a hosted executor is the host) the host hands its workspaces over; once it may host again it
 * tries the workspaces it stood by on at once.
 */
export function followHostRole(
  store: { getState(): { agent: AgentState }; subscribe(listener: () => void): () => void },
  host: Pick<SandboxHost, "handOver" | "retry">
): () => void {
  let seen = store.getState().agent.sandboxHost ?? null;
  let away = standsBy(seen);
  return store.subscribe(() => {
    const role = store.getState().agent.sandboxHost ?? null;
    if (role === seen) return;
    seen = role;
    const now = standsBy(role);
    if (now && !away) void host.handOver();
    else if (!now && away) host.retry();
    away = now;
  });
}

/** How long a preview waits for this tab to take its workspace up: a lease the page before a
 * reload never gave back lapses within the lease (15 s), and the executor then boots. */
export const CLAIM_MS = 60_000;

/**
 * A preview's reader for the workspace: this tab's now if it holds the workspace, else once it has
 * taken it up (`want`); null at once while another tab or a hosted executor is the host (this tab
 * stands by), when that becomes so while it waits, when the host is disposed, on `signal`'s abort,
 * or after `ms`. Before the tab's first `sandbox.host` frame (the server answers hello with it) it
 * waits for that frame rather than take a lease another tab may be elected to.
 */
export function claimForPreview(
  host: Pick<SandboxHost, "reader" | "want" | "onChange" | "isDisposed">,
  role: { get(): HostFrame | null; subscribe(listener: () => void): () => void },
  workspaceId: string,
  timers: Pick<SandboxHostDeps["timers"], "setTimeout" | "clearTimeout">,
  ms = CLAIM_MS,
  signal?: AbortSignal
): Promise<ReturnType<SandboxHost["reader"]>> {
  const now = host.reader(workspaceId);
  if (now || standsBy(role.get()) || host.isDisposed() || signal?.aborted) {
    return Promise.resolve(now);
  }
  return new Promise((resolve) => {
    let open = true;
    let wanting = false;
    let unwant: () => void = () => undefined;
    const stops: (() => void)[] = [() => unwant()];
    const finish = (reader: ReturnType<SandboxHost["reader"]>) => {
      if (!open) return;
      open = false;
      for (const stop of stops) stop();
      resolve(reader);
    };
    const check = () => {
      if (!open) return;
      if (host.isDisposed()) return finish(null);
      const reader = host.reader(workspaceId);
      if (reader) return finish(reader);
      const part = role.get();
      if (standsBy(part)) return finish(null);
      if (!part || wanting) return;
      wanting = true;
      const release = host.want(workspaceId);
      if (open) unwant = release;
      else release(); // settled while it asked
    };
    const abort = () => finish(null);
    signal?.addEventListener("abort", abort);
    stops.push(host.onChange(check), role.subscribe(check), () =>
      signal?.removeEventListener("abort", abort)
    );
    const timer = timers.setTimeout(() => finish(null), ms);
    stops.push(() => timers.clearTimeout(timer));
    check();
  });
}
